import { HttpContextToken, HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { Observable, throwError, timer } from 'rxjs';
import { catchError, retry } from 'rxjs/operators';
import { ApiErrorService } from './api-error.service';
import {
  isRetryableClientError,
  mapHttpErrorToUserMessage,
  parseRetryAfterSeconds,
  shouldShowGlobalApiError,
} from './api-error.mapper';

const MAX_CLIENT_ATTEMPTS = 3;

/**
 * Hosts whose CORS preflight does not list `X-Request-Id` in Access-Control-Allow-Headers.
 * Sending the header to one of them makes the browser preflight the call and then block it
 * outright (net::ERR_FAILED, status 0), which surfaces as a misleading credential/network
 * error rather than a real response — so we skip request-ID tracing for these hosts.
 *
 * The server itself accepts `X-Request-Id` (a direct POST with it succeeds) — it just doesn't list
 * it in the preflight response, and the browser enforces the list, not the server's tolerance.
 *
 * All five hosts are excluded in every environment, deliberately. platform-api / notification-api /
 * custom-links-api advertise only `content-type` everywhere checked. authentication-api is excluded
 * across beta/rc/prod even though prod still permits the header today — see the rollout note below.
 *
 * command-center-api, measured 2026-09-03 on beta / stg / prod (all identical):
 *   access-control-allow-headers: authorization,content-type,x-trace-id
 * `x-request-id` is absent, so it must be withheld or the browser blocks the call. Note that
 * Command Center does accept `x-trace-id` and echoes it back via expose-headers, so tracing
 * could be restored for these hosts by emitting that header instead.
 *
 * ─── MAINTENANCE: this list is hardcoded and does NOT adapt to gateway changes ───
 * Re-verify any host with:
 *   curl -i -X OPTIONS https://<host>/v1/auth/login \
 *     -H 'Origin: https://virtual-agent-rc.truvideo.com' \
 *     -H 'Access-Control-Request-Method: POST' \
 *     -H 'Access-Control-Request-Headers: content-type,x-request-id'
 *
 * authentication-api rollout, observed:
 *   2026-08-12  beta hardened (x-request-id dropped) — beta sign-in broke, beta excluded here
 *   2026-08-21  rc hardened, config now byte-identical to beta — rc sign-in broke
 *   prod        still on the permissive config (`*` + `content-type,x-request-id`) as of 2026-08-22
 * The hardened config is rolling out environment by environment, so prod is excluded pre-emptively
 * rather than reactively — the alternative is discovering this during a live prod sign-in outage and
 * needing a UI redeploy to recover. Excluding it early costs nothing: no AIVA service reads this
 * header on inbound requests (as of 2026-08-22 the only `X-Request-Id` in ai-engine is outbound, in
 * libs/http-client/src/resilient-http.service.ts), so the ID is only ever written to the browser
 * console and no tracing is lost by withholding it from a third-party host.
 *
 * Symptom this prevents: login fails in the browser with net::ERR_FAILED / status 0, surfaced to the
 * user as "Wrong email or password", while the identical request succeeds from Postman/curl — those
 * send no preflight, so they never see the Access-Control-Allow-Headers list the browser enforces.
 *
 * Preferred long-term fix is to invert this blocklist into an allowlist of AIVA-owned hosts
 * (ai-engine-api | aiva-chatbot | interaction-tracking-api | kenility-ai-engine, all of which allow
 * `x-request-id`), so a third-party gateway can change its CORS policy without ever breaking this
 * app. A blocklist encodes a snapshot of someone else's config and fails in production the moment
 * that config changes; an allowlist cannot be broken by a policy we never provoke.
 */
const HOSTS_WITHOUT_REQUEST_ID_CORS =
  /\/\/(?:platform-api|notification-api|custom-links-api|authentication-api|command-center-api)(?:-[a-z0-9]+)?\.truvideo\.com/i;

/**
 * Set on requests that are best-effort/background (e.g. silent auto-enrollment) and already have
 * their own graceful degradation UI, so a failure shouldn't pop the global error toast.
 */
export const SUPPRESS_GLOBAL_API_ERROR = new HttpContextToken<boolean>(() => false);

export const apiErrorInterceptor = (req: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> => {
  const apiErrorService = inject(ApiErrorService);
  const requestId =
    req.headers.get('X-Request-Id') ??
    (typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `${Date.now()}`);

  const skipRequestIdHeader = HOSTS_WITHOUT_REQUEST_ID_CORS.test(req.url);
  const requestWithId = skipRequestIdHeader ? req : req.clone({ setHeaders: { 'X-Request-Id': requestId } });

  const shouldRetryGet = requestWithId.method === 'GET';

  return next(requestWithId).pipe(
    retry({
      count: shouldRetryGet ? MAX_CLIENT_ATTEMPTS - 1 : 0,
      delay: (error: HttpErrorResponse, retryCount) => {
        if (!isRetryableClientError(error.status ?? 0)) {
          throw error;
        }
        const retryAfterSeconds = parseRetryAfterSeconds(error);
        const delayMs = retryAfterSeconds ? retryAfterSeconds * 1000 : Math.min(1000 * Math.pow(2, retryCount - 1), 8000);
        return timer(delayMs);
      },
    }),
    catchError((error: HttpErrorResponse) => {
      const details = mapHttpErrorToUserMessage(error);

      if (shouldShowGlobalApiError(details.status) && !requestWithId.context.get(SUPPRESS_GLOBAL_API_ERROR)) {
        apiErrorService.showError(details);
      }

      console.error(
        JSON.stringify({
          event: 'API_CLIENT_FAILURE',
          endpoint: req.url,
          method: req.method,
          statusCode: details.status,
          requestId: details.requestId ?? requestId,
          timestamp: new Date().toISOString(),
        })
      );

      return throwError(() => error);
    })
  );
};
